import React, { useState, useEffect } from "react";
import { useApp } from "context";
import { Custom } from "components";
import { Grid, IconButton } from "@mui/material";
import { IAppointment, IEvent } from "interfaces";
import { PairValue } from "types";
import { Constants, Enums, Helpers, Store } from "utils";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

type Props = {
  sx?: any;
  appointment: IAppointment | null;
  providers: PairValue[];
  providerEvents: IEvent[];
  health360VirtualEvent: IEvent | null;
  opened: boolean;
  onClose: () => void;
};

export const Health360 = (props: Props) => {
  const { t, theme, locale, service: providedService } = useApp();

  const [health360, setHealth360] = useState<any>({});
  const [health360ToSubmit, setHealth360ToSubmit] = useState<any>({});
  const [isKeyPressed, setIsKeyPressed] = useState(false);
  const [page, setPage] = useState(1);

  const [virtualQare, setVirtualQare] = useState<any>({});

  const [providerDates, setProviderDates] = useState<any[]>([]);
  const [providerTimes, setProviderTimes] = useState<any[]>([]);

  const maxInputsModified = 5;

  const isClinic = Store.sessionStorage.isClinic();

  const getSlots = (start: Date, end: Date) => {
    const arr: PairValue[] = [];
    let index = 0;
    let date = new Date(start);
    while (date < end) {
      const hours = date.getHours();
      const minutes = date.getMinutes();

      const timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;

      arr.push({ key: index++, value: timeString });
      date.setMinutes(date.getMinutes() + 30);
    }
    return arr;
  };

  useEffect(() => {
    setHealth360(props.appointment?.health360 ?? {});
    setHealth360ToSubmit({});
    setIsKeyPressed(false);
    setPage(1);

    const dates = props.providers
      .filter((pv: PairValue) => pv.key !== -1)

      .map((pv: PairValue) => ({
        key: pv.key,

        value:
          props.providerEvents
            .filter((e: IEvent) => e.providerId === pv.key && e.isConfirmed)
            .sort((a, b) => a.start.getTime() - b.start.getTime())
            .map((e: IEvent) => ({ key: e.eId, value: e.start.toLocaleDateString(locale, Constants.DATE_FORMAT) })) ?? [],
      }));

    const times = props.providerEvents.map((e: IEvent) => ({
      key: e.eId,

      value: getSlots(e.start, e.end) ?? [],
    }));

    setProviderDates(dates);
    setProviderTimes(times);

    if (props.health360VirtualEvent) {
      const VirtualStart = props.health360VirtualEvent.start;
      const VirtualEnd = props.health360VirtualEvent.end;

      let foundTimeKey = -1;

      const foundDatekey =
        props.providerEvents.find((e) => {
          if (e.start <= VirtualStart && e.end >= VirtualEnd) {
            foundTimeKey = (VirtualStart.getTime() - e.start.getTime()) / 1800e3;
          }

          return e.start <= VirtualStart && e.end >= VirtualEnd;
        })?.eId ?? -1;

      setVirtualQare({ providerId: props.appointment?.providerId ?? -1, date: foundDatekey, time: foundTimeKey });
    } else {
      setVirtualQare({ providerId: props.appointment?.providerId ?? -1 });
    }
  }, [props.opened]);

  useEffect(() => {
    if (!isKeyPressed && Object.keys(health360ToSubmit).length === maxInputsModified) {
      providedService.health360.upsert(props.appointment?.appointmentId ?? NaN, health360ToSubmit);
      setHealth360ToSubmit({});
      console.log(`auto upsert after ${maxInputsModified} changes`);
      setIsKeyPressed(false);
    }
  }, [health360ToSubmit, isKeyPressed]);

  const handleNumeric = (max: number) => {
    return (event: any) => {
      const fieldName = event.target.name;
      const newValue = event.target.value;
      const newNum = Number(newValue);

      if (isNaN(newValue) || newNum < 0 || newNum > max) return;
      setHealth360((prevVal: any) => ({ ...prevVal, [fieldName]: newValue }));
      setHealth360ToSubmit((prevVal: any) => ({ ...prevVal, [fieldName]: newValue === "" ? -1 : newNum }));
      setIsKeyPressed(true);
    };
  };

  const handleChange = (event: any) => {
    const fieldName = event.target.name;
    const newValue = event.target.value;

    setHealth360((prevVal: any) => ({ ...prevVal, [fieldName]: newValue === -1 ? "" : newValue }));
    setHealth360ToSubmit((prevVal: any) => ({ ...prevVal, [fieldName]: newValue }));
    // "PointerEvent" for Select component
    setIsKeyPressed(event.constructor.name === "SyntheticBaseEvent");
  };

  const handleMultiple = (fieldName: string) => {
    return (_: React.SyntheticEvent<Element, Event>, items: PairValue[] | null) => {
      const numArr: number[] = items?.map((item: PairValue) => item.key) ?? [];
      setHealth360((prevVal: any) => ({ ...prevVal, [fieldName]: numArr }));
      setHealth360ToSubmit((prevVal: any) => ({ ...prevVal, [fieldName]: numArr.length ? JSON.stringify(numArr) : "" }));
      setIsKeyPressed(false);
    };
  };

  const submitHealth360 = async () => {
    if (window.confirm("Are you sure you want to submit?")) {
      const virtualQareToSubmit: any = {};

      if (virtualQare.providerId) virtualQareToSubmit.providerId = virtualQare.providerId;

      const start = (props.providerEvents.find((e) => e.eId === virtualQare.date)?.start.getTime() ?? 0) + virtualQare.time * 1800e3;
      const end = start + 1800e3;

      const startTime = Helpers.toOffsetString(new Date(start));
      const endTime = Helpers.toOffsetString(new Date(end));

      if (!startTime.startsWith("NaN") && !endTime.startsWith("NaN")) {
        virtualQareToSubmit.startTime = startTime;
        virtualQareToSubmit.endTime = endTime;
      }

      const [status, response] = await providedService.health360.upsert(
        props.appointment?.appointmentId ?? NaN,

        { ...health360ToSubmit, ...virtualQareToSubmit }
      );
      if (status === Enums.EnumResponse.Success) props.onClose();
    }
  };

  const deleteHealth360 = async () => {
    if (window.confirm("Are you sure you want to delete?")) {
      const [status, response] = await providedService.health360.delete(props.appointment?.appointmentId ?? NaN);
      if (status === Enums.EnumResponse.Success) props.onClose();
    }
  };

  // TextField Component Generator
  const tf = ({ xs, md, sx, strs, max, req, val, ml }: { xs?: number; md?: number; sx?: any; strs: string[]; max?: number; req?: boolean; val?: any; ml?: boolean }) => {
    return (
      <Grid item xs={xs || 12} md={md} sx={sx}>
        <Custom.TextField
          label={strs[0]}
          name={strs[1]}
          required={req}
          multiline={ml}
          special={ml}
          readOnly={val !== undefined}
          value={val || (health360?.[strs[1]] ?? "")}
          onChange={max ? handleNumeric(max) : handleChange}
          onBlur={() => setIsKeyPressed(false)}
        ></Custom.TextField>
      </Grid>
    );
  };

  // Select Component Generator
  const sl = ({ xs, md, sx, strs, itms, vr }: { xs?: number; md?: number; sx?: any; strs: string[]; itms: PairValue[]; vr?: "filled" | "outlined" }) => {
    return (
      <Grid item xs={xs || 12} md={md} sx={sx}>
        <Custom.Select label={strs[0]} name={strs[1]} items={itms} onDropdownChange={handleChange} value={health360?.[strs[1]]} variant={vr} />
      </Grid>
    );
  };

  // Autocomplete Component Generator
  const ac = ({ xs, md, sx, strs, itms }: { xs?: number; md?: number; sx?: any; strs: string[]; itms: PairValue[] }) => {
    return (
      <Grid item xs={xs || 12} md={md} sx={sx}>
        <Custom.Autocomplete
          label={strs[0]}
          items={itms}
          multiple
          onChange={handleMultiple(strs[1])}
          value={(health360?.[strs[1]] ?? []).map((item: number) => itms.find((x) => x.key === item))}
        />
      </Grid>
    );
  };

  // Section Title Generator
  const sec = (title: string) => {
    return (
      <Grid item xs={12}>
        <h4>{title}</h4>
      </Grid>
    );
  };

  // Column Titles Generator
  const col = (titles: string[], first: boolean = false) => {
    return (
      <>
        {!first && <Grid item xs={12} sx={{ marginTop: "1rem" }}></Grid>}
        <Grid item xs={2}></Grid>
        <Grid item xs={3}>
          {titles[0]}
        </Grid>
        <Grid item xs={3}>
          {titles[1]}
        </Grid>
        <Grid item xs={4}>
          {titles[2]}
        </Grid>
      </>
    );
  };

  // Row Title Generator
  const row = (title: string) => {
    return (
      <Grid item xs={2}>
        <div style={{ paddingTop: "1rem" }}>{title}</div>
      </Grid>
    );
  };

  return (
    <Custom.Modal
      opened={props.opened}
      header={
        <>
          Health 360 ({page}/5)
          <IconButton
            onClick={() => {
              setPage(page === 1 ? 1 : page - 1);
              (document.querySelector(".qh-content.MuiBox-root") as any).scrollTop = 0;
            }}
          >
            <ArrowBackIosIcon sx={{ color: page === 1 ? theme.color.disabled.color : theme.palette.font.color }} />
          </IconButton>
          <IconButton
            onClick={() => {
              setPage(page === 5 ? 5 : page + 1);
              (document.querySelector(".qh-content.MuiBox-root") as any).scrollTop = 0;
            }}
          >
            <ArrowForwardIosIcon sx={{ color: page === 5 ? theme.color.disabled.color : theme.palette.font.color }} />
          </IconButton>
        </>
      }
      onClose={props.onClose}
    >
      <Grid container spacing={theme.margin.sm}>
        {tf({ md: 6, strs: [t.label.name, ""], val: Helpers.replaceComma(props.appointment?.username ?? "") })}
        {tf({ md: 6, strs: [t.label.check_in_datetime, ""], val: new Date(props.appointment?.checkInTime ?? "").toLocaleTimeString(locale, Constants.DATE_FORMAT) })}
        {sec("")}

        {page === 1 && (
          <>
            {isClinic && (
              <>
                {sec("Virtual Call")}

                <Grid item xs={12} md={4}>
                  <Custom.Select
                    label={"Provider"}
                    items={props.providers}
                    onDropdownChange={(e) => {
                      setVirtualQare({ providerId: e.target.value });
                    }}
                    value={virtualQare.providerId}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Custom.Select
                    label={"Date"}
                    items={providerDates.find((pv: PairValue) => pv.key === virtualQare?.providerId ?? -1)?.value ?? []}
                    onDropdownChange={(e) => {
                      setVirtualQare({ providerId: virtualQare.providerId, date: e.target.value });
                    }}
                    value={virtualQare.date}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Custom.Select
                    label={"Time"}
                    items={providerTimes.find((pv: PairValue) => pv.key === virtualQare?.date ?? -1)?.value ?? []}
                    onDropdownChange={(e) => {
                      setVirtualQare({ ...virtualQare, time: e.target.value });
                    }}
                    value={virtualQare.time}
                  />
                </Grid>

                {sec("")}
              </>
            )}

            {tf({ md: 12, strs: ["Consent Obtained by:", "consent"] })}
            {tf({ md: 3, strs: ["Ethnicity", "ethnicity"] })}
            {tf({ md: 3, strs: ["Education", "education"] })}
            {tf({ md: 3, strs: ["Profession", "profession"] })}
            {sl({ md: 3, strs: ["Living Arrangement", "livingArrangement"], itms: Constants.health360.livingArrangementsPV })}

            {sec("Complete Health History")}

            {tf({ md: 12, strs: ["Head Injury, Loss of consciousness", "headInjury"] })}
            {tf({ md: 12, strs: ["Frequent or severe headaches", "headaches"] })}
            {tf({ md: 12, strs: ["Psychiatric diseases", "psychiatricDiseases"] })}
            {tf({ md: 12, strs: ["Neurological diseases", "neurologicalDiseases"] })}
            {tf({ md: 12, strs: ["Ear problems or deafness", "earProblems"] })}
            {tf({ md: 12, strs: ["Metabolic diseases", "metabolicDiseases"] })}
            {tf({ md: 12, strs: ["Respiratory diseases", "respiratoryDiseases"] })}
            {tf({ md: 12, strs: ["Cardiovascular diseases", "cardiovascularDiseases"] })}
            {tf({ md: 12, strs: ["Gastrointestinal diseases", "gastrointestinalDiseases"] })}
            {tf({ md: 12, strs: ["Musculoskeletal disorders", "musculoskeletalDisorders"] })}
            {tf({ md: 12, strs: ["Menstrual disorders", "menstrualDisorders"] })}
            {tf({ md: 12, strs: ["Any other medical condition", "otherMedicalCondition"] })}
            {tf({ md: 12, strs: ["Current Medications", "currentMedications"] })}
            {tf({ md: 12, strs: ["Allergies", "allergies"] })}
            {tf({ md: 6, strs: ["Smoking (weekly)", "smokingWeekly"], max: 100 })}
            {tf({ md: 6, strs: ["Alcohol use (weekly)", "alcoholWeekly"], max: 100 })}
            {tf({ md: 12, strs: ["Health History Notes", "healthHistoryNotes"], ml: true })}
          </>
        )}

        {page === 2 && (
          <>
            {sec("Physical Health")}

            {tf({ md: 4, strs: ["Weight (kg)", "wt"], max: 1000 })}
            {tf({ md: 4, strs: ["Height (cm)", "ht"], max: 1000 })}
            {tf({ md: 4, strs: ["BMI", "BMI"], val: health360?.wt && health360?.ht ? ((Number(health360?.wt) / Number(health360?.ht) ** 2) * 10000).toFixed(1) : "" })}

            {tf({ md: 3, strs: ["Heart Rate", "heartRate"], max: 1000 })}
            {tf({ md: 3, strs: ["Systolic BP", "sbp"], max: 1000 })}
            {tf({ md: 3, strs: ["Diastolic BP", "dbp"], max: 1000 })}
            {tf({ md: 3, strs: ["Mean BP", "meanBP"], val: health360?.sbp && health360?.dbp ? ((Number(health360?.sbp) + 2 * Number(health360?.dbp)) / 3).toFixed(0) : "" })}

            {sl({ md: 6, strs: ["Vision (Left Eye)", "visionLeft"], itms: Constants.health360.visionsPV })}
            {sl({ md: 6, strs: ["Vision (Right Eye)", "visionRight"], itms: Constants.health360.visionsPV })}
            {sl({ md: 4, strs: ["Hearing - Whisper test", "whisperTest"], itms: Constants.health360.successFailurePV })}
            {sl({ md: 4, strs: ["Hearing - Weber test", "weberTest"], itms: Constants.health360.successFailurePV })}
            {sl({ md: 4, strs: ["Hearing - Rinne test", "rinneTest"], itms: Constants.health360.successFailurePV })}
            {sl({ md: 6, strs: ["Upper Extremities", "upperXtr"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Lower Extremities", "lowerXtr"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Extremities Hx", "xtrHx"], itms: Constants.health360.historiesPV })}
            {tf({ md: 6, strs: ["Extremities Hx Desc", "xtrHxDesc"] })}
            {sl({ md: 6, strs: ["Heart Exam", "heartExam"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Lungs Exam", "lungsExam"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Chest Hx", "chestHx"], itms: Constants.health360.historiesPV })}
            {tf({ md: 6, strs: ["Chest Hx Desc", "chestHxDesc"] })}
            {sl({ md: 6, strs: ["Supra Pelvic Exam", "supraPelvicExam"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Abdomen Exam", "abdomenExam"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Abdo-Pelvic Hx", "abdoPelvicHx"], itms: Constants.health360.historiesPV })}
            {tf({ md: 6, strs: ["Abdo-Pelvic Hx Desc", "abdoPelvicHxDesc"] })}
            {sl({ md: 6, strs: ["Cranial Exam", "cranialExam"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Neuro Exam", "neuroExam"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["Neuro Cranial Hx", "neuroCranialHx"], itms: Constants.health360.historiesPV })}
            {tf({ md: 6, strs: ["Neuro Cranial Hx Desc", "neuroCranialHxDesc"] })}
            {tf({ md: 12, strs: ["Physical Health Notes", "physicalHealthNotes"], ml: true })}
          </>
        )}

        {page === 3 && (
          <>
            {sec("Healthy Lifestyle")}

            {sl({ md: 4, strs: ["Sexual Orientation", "sexualOrientation"], itms: Constants.health360.sexualOrientationsPV })}
            {tf({ md: 4, strs: ["Partners", "partners"], max: 100 })}
            {sl({ md: 4, strs: ["Protection", "protection"], itms: Constants.health360.scoresPV })}
            {sl({ md: 6, strs: ["STI Hx", "stiHx"], itms: Constants.health360.historiesPV })}
            {tf({ md: 6, strs: ["STI Hx Desc", "stiHxDesc"] })}
            {sl({ md: 4, strs: ["Erectile Function Rating", "erectileFunction"], itms: Constants.health360.scoresPV })}
            {sl({ md: 4, strs: ["Menstrual Regularity Rating", "mensReg"], itms: Constants.health360.scoresPV })}
            {sl({ md: 4, strs: ["Libido/Drive", "libido"], itms: Constants.health360.scoresPV })}
            {sl({ md: 4, strs: ["Quantity of Food", "foodQuantity"], itms: Constants.health360.foodQuantitiesPV })}
            {sl({ md: 4, strs: ["Quality of Food", "foodQuality"], itms: Constants.health360.foodQualitiesPV })}
            {sl({ md: 4, strs: ["Timing of Meals", "mealTiming"], itms: Constants.health360.regularitiesPV })}
            {sl({ md: 6, strs: ["Type of Physical Activity", "activityType"], itms: Constants.health360.activitiesPV })}
            {tf({ md: 6, strs: ["Duration of Activity", "activityDuration"], max: 1000 })}
            {sl({ md: 6, strs: ["Timing of Sleep", "sleepTiming"], itms: Constants.health360.sleepTimingsPV })}
            {sl({ md: 6, strs: ["Duration of Sleep", "sleepDuration"], itms: Constants.health360.scoresPV })}
            {tf({ md: 12, strs: ["Healthy Lifestyle Notes", "healthyLifestyleNotes"], ml: true })}
          </>
        )}

        {page === 4 && (
          <>
            {sec("Substances")}

            {col(["Caffeine", "Nicotine", "Alcohol"], true)}

            {row("Daily Amount")}
            {tf({ xs: 3, strs: ["# Cups", "caffeineAmount"], max: 100 })}
            {tf({ xs: 3, strs: ["# Cigarettes", "nicotineAmount"], max: 100 })}
            {tf({ xs: 3, strs: ["# Servings", "alcoholAmount"], max: 100 })}

            {row("Per Week")}
            {tf({ xs: 3, strs: ["", "caffeineFreq"], max: 100 })}
            {tf({ xs: 3, strs: ["", "nicotineFreq"], max: 100 })}
            {tf({ xs: 3, strs: ["", "alcoholFreq"], max: 100 })}

            {col(["Marijuana", "Amphetamines", "Hallucinogens"])}

            {row("Daily Amount")}
            {tf({ xs: 3, strs: ["", "marijuanaAmount"], max: 1000 })}
            {tf({ xs: 3, strs: ["", "amphetaminesAmount"], max: 1000 })}
            {tf({ xs: 3, strs: ["", "hallucinogensAmount"], max: 1000 })}

            {row("Per Week")}
            {tf({ xs: 3, strs: ["", "marijuanaFreq"], max: 100 })}
            {tf({ xs: 3, strs: ["", "amphetaminesFreq"], max: 100 })}
            {tf({ xs: 3, strs: ["", "hallucinogensFreq"], max: 100 })}

            {row("Last Used")}
            {sl({ xs: 3, strs: ["", "marijuanaLastUsed"], itms: Constants.health360.drugHistoriesPV, vr: "filled" })}
            {sl({ xs: 3, strs: ["", "amphetaminesLastUsed"], itms: Constants.health360.drugHistoriesPV, vr: "filled" })}
            {sl({ xs: 3, strs: ["", "hallucinogensLastUsed"], itms: Constants.health360.drugHistoriesPV, vr: "filled" })}

            {col(["Cocaine/Crack", "Heroin", "Rx Med Addiction"])}

            {row("Daily Amount")}
            {tf({ xs: 3, strs: ["", "cocaineCrackAmount"], max: 1000 })}
            {tf({ xs: 3, strs: ["", "heroinAmount"], max: 1000 })}
            {tf({ xs: 3, strs: ["", "rxMedAddictionAmount"], max: 1000 })}

            {row("Per Week")}
            {tf({ xs: 3, strs: ["", "cocaineCrackFreq"], max: 100 })}
            {tf({ xs: 3, strs: ["", "heroinFreq"], max: 100 })}
            {tf({ xs: 3, strs: ["", "rxMedAddictionFreq"], max: 100 })}

            {row("Last Used")}
            {sl({ xs: 3, strs: ["", "cocaineCrackLastUsed"], itms: Constants.health360.drugHistoriesPV, vr: "filled" })}
            {sl({ xs: 3, strs: ["", "heroinLastUsed"], itms: Constants.health360.drugHistoriesPV, vr: "filled" })}
            {sl({ xs: 3, strs: ["", "rxMedAddictionLastUsed"], itms: Constants.health360.drugHistoriesPV, vr: "filled" })}

            {tf({ md: 12, strs: ["Substances Notes", "substancesNotes"], ml: true })}
          </>
        )}

        {page === 5 && (
          <>
            {sec("Mental Health")}

            {ac({ md: 12, strs: ["Orientation", "orientation"], itms: Constants.health360.orientationsPV })}
            {ac({ md: 12, strs: ["Speech", "speech"], itms: Constants.health360.speechesPV })}
            {ac({ md: 12, strs: ["Thought Process", "thoughtProcess"], itms: Constants.health360.thoughtProcessesPV })}
            {ac({ md: 12, strs: ["Perceptual Process", "perceptualProcess"], itms: Constants.health360.perceptualProcessesPV })}
            {ac({ md: 12, strs: ["Judgement Issues", "judgementIssues"], itms: Constants.health360.judgementIssuesPV })}
            {ac({ md: 12, strs: ["Mood Disroders", "moodDisroders"], itms: Constants.health360.moodDisrodersPV })}
            {ac({ md: 12, strs: ["Memory Disorders", "memoryDisorders"], itms: Constants.health360.memoryDisordersPV })}
            {ac({ md: 12, strs: ["Emotional Affect", "emotionalAffect"], itms: Constants.health360.emotionalAffectPV })}
            {ac({ md: 12, strs: ["Mental Deficits", "mentalDeficits"], itms: Constants.health360.mentalDeficitsPV })}
            {tf({ md: 6, strs: ["GAD7 Scoring (Anxiety)", "GAD7Score"], max: 21 })}
            {tf({ md: 6, strs: ["PHQ9 Scoring (Depression)", "PHQ9Score"], max: 27 })}
            {tf({ md: 12, strs: ["Mental Health Notes", "mentalHealthNotes"], ml: true })}
          </>
        )}
      </Grid>
      <div style={{ marginTop: "0.75rem" }}>
        <Custom.Button size="large" onClick={submitHealth360}>
          {t.action.submit}
        </Custom.Button>
        {/* <Custom.Button sx={{ float: "right" }} size="large" onClick={deleteHealth360}>
          {t.action.delete}
        </Custom.Button> */}
      </div>
    </Custom.Modal>
  );
};
